import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { phaseApi, stripeApi } from '../services/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import ChecklistPhase from '../components/ChecklistPhase';
import PhaseNotes, { PhaseAtcCard } from '../components/PhaseNotes';
import McduTutorial from '../components/McduTutorial';
import PhaseProgressBar from '../components/PhaseProgressBar';
import PaywallModal from '../components/PaywallModal';
import { PHASE_GROUPS } from '../components/PhaseNavigation';
import { useAssistancePanel } from '../contexts/AssistancePanelContext';
import { useSidebar } from '../contexts/SidebarContext';
import { useViewportSize } from '../hooks/useViewportSize';
import {
  ChevronLeft, ChevronRight, RotateCcw, Lock, List,
  Loader2, AlertTriangle, X, Check
} from 'lucide-react';

const groupNameFor = (orderIndex) => {
  const group = PHASE_GROUPS.find(g => orderIndex >= g.min && orderIndex <= g.max);
  return group ? group.name : '';
};

const LOCKED_FEATURES = [
  "All 21 phases, Pre-Flight to Go Around, in correct flow order",
  "Full MCDU setup tutorial, step by step",
  "Explanatory notes where the \"why\" matters",
  "In-Flight Assistance: ECAM fault lookup with quick rectification steps",
  "One-time payment. Lifetime access, no subscription."
];

const LockedPhaseView = (props) => {
  const navigate = props.navigate;
  const onUnlock = props.onUnlock;
  const freePhases = props.freePhases || [];
  const [stripePrice, setStripePrice] = useState(null);

  useEffect(() => {
    stripeApi.getPrice().then(res => setStripePrice(res.data)).catch(() => {});
  }, []);

  const displayPrice = stripePrice
    ? `${stripePrice.currency === 'GBP' ? '£' : stripePrice.currency + ' '}${stripePrice.price.toFixed(2)}`
    : null;

  return (
    <div className="max-w-lg mx-auto px-5 py-10 md:py-16" data-testid="locked-phase-view">
      <div
        className="w-14 h-14 rounded-[14px] flex items-center justify-center mb-6"
        style={{ background: 'var(--gc-current-row)', border: '1px solid #1F5566' }}
      >
        <Lock className="w-6 h-6" style={{ color: 'var(--gc-cyan)' }} strokeWidth={1.8} />
      </div>

      <div className="font-plex-mono text-xs font-semibold tracking-[0.08em] mb-3" style={{ color: 'var(--gc-green)' }} data-testid="locked-kicker">
        {freePhases.map((p, i) => (
          <span key={p.id}>
            {p.title.toUpperCase()} {p.is_complete ? '✓' : ''}
            {i < freePhases.length - 1 ? ' · ' : ''}
          </span>
        ))}
      </div>

      <h1
        className="font-barlow-condensed font-bold text-[36px] md:text-[44px] dt:text-[50px] leading-[1.05] mb-4"
        style={{ color: 'var(--gc-text-main)' }}
        data-testid="locked-heading"
      >
        Your APU's running.<br />Now fly the rest.
      </h1>

      <p className="text-base leading-relaxed mb-6" style={{ color: 'var(--gc-text-body)' }}>
        Unlock all 21 phases and take the Fenix from the gate to shutdown, in the right order, every time.
      </p>

      <div className="rounded-gc-card border overflow-hidden mb-6" style={{ borderColor: 'var(--gc-border-card)', background: 'var(--gc-checklist-card)' }}>
        {LOCKED_FEATURES.map((feature, index) => (
          <div
            key={index}
            className="flex items-start gap-3 px-4 py-3 border-b last:border-0"
            style={{ borderColor: 'var(--gc-border-row)' }}
          >
            <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--gc-green)' }} />
            <span className="text-sm leading-relaxed" style={{ color: 'var(--gc-text-body)' }}>{feature}</span>
          </div>
        ))}
      </div>

      <button
        onClick={onUnlock}
        className="w-full h-14 rounded-gc-button font-barlow-condensed font-bold text-xl flex items-center justify-center gap-2 mb-6"
        style={{ background: 'var(--gc-cyan)', color: 'var(--gc-cyan-text-on)' }}
        data-testid="unlock-full-access-btn"
      >
        {displayPrice ? `Unlock Full Access · ${displayPrice}` : 'Unlock Full Access'}
      </button>

      <div className="font-plex-mono text-[11px] text-center tracking-[0.08em] mb-8" style={{ color: 'var(--gc-text-muted)' }}>
        ONE PAYMENT · LIFETIME ACCESS · NO SUBSCRIPTION
      </div>

      <button
        onClick={() => navigate('/dashboard')}
        className="w-full h-11 rounded-gc-button border text-sm font-medium transition-colors"
        style={{ borderColor: 'var(--gc-border-outline)', color: 'var(--gc-text-body)' }}
        data-testid="go-dashboard-btn"
      >
        Go to Dashboard
      </button>
    </div>
  );
};

const PhasePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isOpen: isAssistanceOpen } = useAssistancePanel();
  const { isOpen: isSidebarOpen } = useSidebar();
  const { width: viewportWidth } = useViewportSize();

  const [phase, setPhase] = useState(null);
  const [allPhases, setAllPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPhaseList, setShowPhaseList] = useState(false);
  const [showResetFlightConfirm, setShowResetFlightConfirm] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const notesRef = useRef(null);

  const fetchPhases = useCallback(async () => {
    try {
      const res = await phaseApi.getAll();
      setAllPhases(res.data);
    } catch (err) {
      console.error('Failed to fetch phases', err);
    }
  }, []);

  const fetchPhase = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await phaseApi.getBySlug(slug);
      setPhase(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('locked');
      } else if (err.response?.status === 404) {
        setError('not_found');
      } else {
        setError('error');
      }
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchPhases();
  }, [fetchPhases]);

  useEffect(() => {
    fetchPhase();
  }, [fetchPhase]);

  // Navigation helpers
  const currentIndex = allPhases.findIndex(p => p.slug === slug);
  const prevPhase = currentIndex > 0 ? allPhases[currentIndex - 1] : null;
  const nextPhase = currentIndex < allPhases.length - 1 ? allPhases[currentIndex + 1] : null;

  const handleToggleTick = async (itemIndex, ticked) => {
    // Optimistic update
    setPhase(prev => ({
      ...prev,
      checklist_items: prev.checklist_items.map(item =>
        item.order_index === itemIndex ? { ...item, ticked } : item
      )
    }));

    try {
      await phaseApi.toggleTick(slug, itemIndex, ticked);
      fetchPhases();
    } catch {
      fetchPhase();
    }
  };

  const handleResetPhase = async () => {
    setResetting(true);
    try {
      await phaseApi.resetPhase(slug);
      await fetchPhase();
      await fetchPhases();
    } finally {
      setResetting(false);
    }
  };

  const handleResetFlight = async () => {
    setResetting(true);
    try {
      await phaseApi.resetAll();
      setShowResetFlightConfirm(false);
      await fetchPhase();
      await fetchPhases();
    } finally {
      setResetting(false);
    }
  };

  // Phase complete check
  const isPhaseComplete = phase?.checklist_items?.every(i => i.ticked) && phase?.checklist_items?.length > 0;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--gc-cyan)' }} />
        </div>
      </DashboardLayout>
    );
  }

  if (error === 'locked') {
    const freePhases = allPhases.filter(p => !p.is_locked).slice(0, 2);
    return (
      <DashboardLayout>
        <LockedPhaseView navigate={navigate} onUnlock={() => setShowPaywall(true)} freePhases={freePhases} />
        <PaywallModal isOpen={showPaywall} onClose={() => setShowPaywall(false)} />
      </DashboardLayout>
    );
  }

  if (error || !phase) {
    return (
      <DashboardLayout>
        <div className="max-w-lg mx-auto text-center py-20 px-4">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--gc-amber)' }} />
          <h2 className="font-barlow-condensed font-bold text-xl mb-2" style={{ color: 'var(--gc-text-main)' }}>Phase Not Found</h2>
          <button
            onClick={() => navigate('/dashboard')}
            className="h-11 px-5 rounded-gc-button font-semibold"
            style={{ background: 'var(--gc-cyan)', color: 'var(--gc-cyan-text-on)' }}
          >
            Go to Dashboard
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const ticked = phase.checklist_items?.filter(i => i.ticked).length || 0;
  const total = phase.checklist_items?.length || 0;
  const left = total - ticked;
  const regularNotes = (phase.flow_steps || []).filter(n => (n.type || '').toUpperCase() !== 'ATC');
  const atcEntries = (phase.flow_steps || []).filter(n => (n.type || '').toUpperCase() === 'ATC');
  const hasNotes = regularNotes.length > 0 || atcEntries.length > 0;

  // Same reflow rule as the checklist/assistance layout: only show the notes
  // rail as a side-by-side column when there's still comfortable room left
  // after the left sidebar + its own 380px width. Otherwise fall back to
  // the existing inline-below-checklist rendering (same as when the
  // Assistance panel itself is open).
  const notesSidebarSpace = viewportWidth >= 1024 && isSidebarOpen ? 268 : 0;
  const notesSideBySideOk = (viewportWidth - notesSidebarSpace - 380 - 80) >= 640;
  const forceInlineNotes = isAssistanceOpen || !notesSideBySideOk;

  return (
    <DashboardLayout>
      <div className="dt:flex">
        {/* Main column */}
        <div className="flex-1 min-w-0 px-4 md:px-8 py-6 md:py-8 pb-28 dt:pb-10 max-w-3xl mx-auto dt:mx-0 dt:max-w-none dt:px-10">
          {/* Kicker + title row */}
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-4">
                <span className="font-plex-mono text-[13px] font-semibold tracking-[0.08em]" style={{ color: 'var(--gc-cyan)' }}>
                  PHASE {String(phase.order_index).padStart(2, '0')} / 21
                </span>
                <span className="font-plex-mono text-xs tracking-[0.08em]" style={{ color: 'var(--gc-text-muted)' }}>
                  {groupNameFor(phase.order_index)}
                </span>
              </div>
              <h1
                className="font-barlow-condensed font-bold text-[34px] md:text-[42px] dt:text-[48px] leading-none"
                style={{ color: 'var(--gc-text-main)' }}
                data-testid="phase-title"
              >
                {phase.title}
              </h1>
            </div>
            <button
              onClick={() => setShowPhaseList(!showPhaseList)}
              className="p-2.5 rounded-gc-button border transition-colors flex-shrink-0"
              style={{ borderColor: 'var(--gc-border-outline)', color: 'var(--gc-text-body)' }}
              title="Phase list"
              data-testid="phase-list-toggle"
            >
              <List className="w-5 h-5" />
            </button>
          </div>

          {/* Phase list dropdown */}
          {showPhaseList && (
            <div className="mb-6 rounded-gc-card border overflow-hidden" style={{ borderColor: 'var(--gc-border-card)', background: 'var(--gc-card)' }} data-testid="phase-list-panel">
              <div className="flex items-center justify-between px-3 py-2 border-b" style={{ borderColor: 'var(--gc-border-row)' }}>
                <span className="font-plex-mono text-xs uppercase tracking-wider" style={{ color: 'var(--gc-text-muted)' }}>All Phases</span>
                <button onClick={() => setShowPhaseList(false)} style={{ color: 'var(--gc-text-muted)' }}>
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {allPhases.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => { navigate(`/phases/${p.slug}`); setShowPhaseList(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors border-b"
                    style={{ borderColor: 'var(--gc-border-row)', background: p.slug === slug ? 'var(--gc-current-row)' : 'transparent' }}
                    data-testid={`phase-nav-${p.slug}`}
                  >
                    <span className="font-plex-mono text-xs w-5 text-right" style={{ color: 'var(--gc-text-faint)' }}>
                      {p.order_index}
                    </span>
                    <span className="flex-1 text-sm font-medium" style={{ color: p.slug === slug ? 'var(--gc-cyan)' : 'var(--gc-text-main)' }}>
                      {p.title}
                    </span>
                    {p.is_locked && <Lock className="w-3.5 h-3.5" style={{ color: 'var(--gc-text-muted)' }} />}
                    {p.is_complete && !p.is_locked && <Check className="w-3.5 h-3.5" style={{ color: 'var(--gc-green)' }} />}
                    {!p.is_complete && !p.is_locked && (
                      <span className="font-plex-mono text-[10px]" style={{ color: 'var(--gc-text-faint)' }}>
                        {p.ticked_count}/{p.item_count}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 21-segment progress bar */}
          <div className="mb-4">
            <PhaseProgressBar phases={allPhases} currentOrderIndex={phase.order_index} />
          </div>

          {/* ECAM memo */}
          {total > 0 && (
            <div
              className="flex items-center justify-between rounded-gc-card border px-4 md:px-[18px] py-3.5 mb-5"
              style={{ borderColor: 'var(--gc-border-card)', background: 'var(--gc-card)' }}
              data-testid="ecam-memo"
            >
              <div className="flex flex-col gap-0.5">
                <span className="font-plex-mono text-[11px] tracking-[0.08em]" style={{ color: 'var(--gc-text-muted)' }}>ECAM MEMO</span>
                <span className="font-plex-mono text-sm font-semibold" style={{ color: isPhaseComplete ? 'var(--gc-green)' : 'var(--gc-cyan)' }}>
                  {isPhaseComplete ? 'NO BLUE — PHASE COMPLETE' : `${left} ITEM${left === 1 ? '' : 'S'} IN BLUE`}
                </span>
              </div>
              <div className="font-barlow-condensed font-semibold text-[30px]">
                <span style={{ color: isPhaseComplete ? 'var(--gc-green)' : 'var(--gc-text-main)' }}>{ticked}</span>
                <span style={{ color: 'var(--gc-text-faint)' }}> / {total}</span>
              </div>
            </div>
          )}

          {/* Quick-jump to notes, shown only when Assistance is open and notes have moved below the checklist */}
          {hasNotes && forceInlineNotes && (
            <button
              onClick={() => notesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="font-plex-mono text-[11px] tracking-[0.04em] mb-5 -mt-2 block"
              style={{ color: 'var(--gc-cyan)' }}
              data-testid="jump-to-notes-link"
            >
              {regularNotes.length} NOTE{regularNotes.length === 1 ? '' : 'S'}{atcEntries.length > 0 ? ' · ATC' : ''} ↓
            </button>
          )}

          {/* Checklist */}
          <ChecklistPhase phase={phase} onToggleTick={handleToggleTick} />

          {/* MCDU long-form tutorial (Stage 3) */}
          {phase.mode === 'tutorial' && phase.tutorial && (
            <McduTutorial tutorial={phase.tutorial} />
          )}

          {/* Notes + ATC below checklist when assistance panel open, or below "dt" breakpoint */}
          {hasNotes && (
            <div ref={notesRef} className={forceInlineNotes ? 'block mt-8' : 'dt:hidden mt-8'}>
              <div className="font-plex-mono text-[11px] tracking-[0.1em] mb-3" style={{ color: 'var(--gc-text-muted)' }}>
                WHY IT MATTERS
              </div>
              <div className="flex flex-col gap-3">
                <PhaseNotes notes={phase.flow_steps} />
                {atcEntries.map(atc => <PhaseAtcCard key={atc.id} atc={atc} />)}
              </div>
            </div>
          )}

          {/* Reset controls */}
          <div className="flex items-center gap-2 mt-8">
            <button
              onClick={handleResetPhase}
              disabled={resetting}
              className="flex items-center gap-1.5 h-9 px-3 text-xs rounded-gc-button border transition-colors"
              style={{ borderColor: 'var(--gc-border-outline)', color: 'var(--gc-text-muted)' }}
              title="Reset this phase"
              data-testid="reset-phase-btn"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Reset Phase</span>
            </button>
            <button
              onClick={() => setShowResetFlightConfirm(true)}
              disabled={resetting}
              className="flex items-center gap-1.5 h-9 px-3 text-xs rounded-gc-button border transition-colors"
              style={{ borderColor: 'var(--gc-border-outline)', color: '#C0695F' }}
              title="Reset all 21 phases"
              data-testid="reset-flight-btn"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Reset Flight</span>
            </button>
          </div>

          {/* Navigation: in-flow at md+, fixed bottom bar below md */}
          <div
            className="fixed md:static bottom-0 left-0 right-0 md:mt-6 px-4 py-3 md:p-0 z-30 border-t md:border-0"
            style={{ background: 'var(--gc-bg)', borderColor: 'var(--gc-border-nav)' }}
          >
            {isPhaseComplete && nextPhase && (
              <div className="text-center mb-2 md:hidden">
                <span className="text-xs font-medium" style={{ color: 'var(--gc-green)' }} data-testid="phase-complete-msg">
                  Phase complete — proceed to {nextPhase.title}
                </span>
              </div>
            )}
            <div className="flex items-center gap-3">
              <button
                onClick={() => prevPhase && navigate(`/phases/${prevPhase.slug}`)}
                disabled={!prevPhase}
                className="flex-1 h-14 rounded-gc-button border flex flex-col items-start justify-center px-[18px] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                style={{ borderColor: 'var(--gc-border-outline)', color: 'var(--gc-text-body)' }}
                data-testid="prev-phase-btn"
              >
                <span className="font-plex-mono text-[10px] tracking-[0.08em]" style={{ color: 'var(--gc-text-muted)' }}>‹ PREVIOUS</span>
                <span className="font-barlow-condensed font-semibold text-lg truncate max-w-full">{prevPhase ? prevPhase.title : '—'}</span>
              </button>
              <button
                onClick={() => nextPhase && !nextPhase.is_locked && navigate(`/phases/${nextPhase.slug}`)}
                disabled={!nextPhase || (nextPhase && nextPhase.is_locked)}
                className="flex-[1.3] h-14 rounded-gc-button border flex flex-col items-end justify-center px-[18px] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                style={
                  isPhaseComplete && nextPhase && !nextPhase.is_locked
                    ? { background: 'var(--gc-green)', borderColor: 'var(--gc-green)', color: 'var(--gc-green-text-on)' }
                    : { background: 'var(--gc-card)', borderColor: 'var(--gc-border-outline)', color: 'var(--gc-text-main)' }
                }
                data-testid="next-phase-btn"
              >
                <span
                  className="font-plex-mono text-[10px] tracking-[0.08em]"
                  style={{ color: isPhaseComplete && nextPhase && !nextPhase.is_locked ? 'var(--gc-green-text-on)' : 'var(--gc-text-muted)' }}
                >
                  NEXT ›
                </span>
                <span className="font-barlow-condensed font-semibold text-lg truncate max-w-full">{nextPhase ? nextPhase.title : '—'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Right column notes - desktop only, when assistance panel is closed */}
        {hasNotes && (
          <aside
            className={forceInlineNotes ? 'hidden' : 'hidden dt:flex dt:w-[380px] dt:flex-shrink-0 flex-col gap-3 px-6 py-8 overflow-y-auto border-l'}
            style={{ borderColor: 'var(--gc-border-nav)' }}
            data-testid="phase-notes-sidebar"
          >
            <div className="font-plex-mono text-[11px] tracking-[0.1em]" style={{ color: 'var(--gc-text-muted)' }}>
              WHY IT MATTERS
            </div>
            <PhaseNotes notes={phase.flow_steps} />
            {atcEntries.map(atc => <PhaseAtcCard key={atc.id} atc={atc} />)}
          </aside>
        )}
      </div>

      {/* Reset Flight confirmation modal */}
      {showResetFlightConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="reset-flight-modal">
          <div className="rounded-gc-card border max-w-sm w-full p-6" style={{ background: 'var(--gc-card)', borderColor: 'var(--gc-border-card)' }}>
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 shrink-0 mt-0.5" style={{ color: '#C0695F' }} />
              <div>
                <h3 className="font-barlow-condensed font-bold text-lg mb-1" style={{ color: 'var(--gc-text-main)' }}>Reset Flight?</h3>
                <p className="text-sm" style={{ color: 'var(--gc-text-body)' }}>
                  This will clear all ticks across all 21 phases. This cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowResetFlightConfirm(false)}
                className="px-4 py-2 text-sm rounded-gc-button border transition-colors"
                style={{ borderColor: 'var(--gc-border-outline)', color: 'var(--gc-text-body)' }}
                data-testid="reset-flight-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleResetFlight}
                disabled={resetting}
                className="px-4 py-2 text-sm rounded-gc-button disabled:opacity-50 transition-colors flex items-center gap-2"
                style={{ background: '#C0695F', color: '#2B0D0A' }}
                data-testid="reset-flight-confirm"
              >
                {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                Reset All Phases
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default PhasePage;
